import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Download, LogIn } from 'lucide-react';
import { trackClick } from '../../services/landingAnalytics';

export const Header = ({ onInstallClick }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'For You', href: '#audience' },
    { label: 'How it works', href: '#how' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <header
      data-testid="landing-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/80 border-b border-slate-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5" data-testid="brand-logo-link">
          <img src="/icon-192.png" alt="" className="w-9 h-9 rounded-lg" />
          <span
            className="text-lg font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            GateKeeper <span className="text-[#14D4C6]">Pro</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => {
              trackClick('header-login', 'header');
              navigate('/login');
            }}
            className="text-sm font-medium text-slate-700 hover:text-slate-900 px-4 py-2 rounded-md transition-colors flex items-center gap-1.5"
            data-testid="header-login-btn"
          >
            <LogIn className="w-4 h-4" /> Login
          </button>
          <button
            onClick={() => {
              trackClick('header-install', 'header');
              onInstallClick?.();
            }}
            className="text-sm font-semibold text-slate-900 bg-[#14D4C6] hover:bg-[#10B5A9] px-5 py-2 rounded-md transition-all flex items-center gap-1.5 shadow-sm hover:shadow-md"
            data-testid="header-install-btn"
          >
            <Download className="w-4 h-4" /> Install App
          </button>
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden border-t border-slate-200 bg-white px-6 py-4 space-y-3"
          data-testid="mobile-menu"
        >
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-slate-700"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {l.label}
            </a>
          ))}
          <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
            <button
              onClick={() => {
                trackClick('mobile-login', 'header');
                navigate('/login');
              }}
              className="text-sm font-medium text-slate-700 px-4 py-2 rounded-md border border-slate-200 text-left flex items-center gap-2"
              data-testid="mobile-login-btn"
            >
              <LogIn className="w-4 h-4" /> Login
            </button>
            <button
              onClick={() => {
                trackClick('mobile-install', 'header');
                setMobileOpen(false);
                onInstallClick?.();
              }}
              className="text-sm font-semibold text-slate-900 bg-[#14D4C6] px-4 py-2 rounded-md flex items-center gap-2"
              data-testid="mobile-install-btn"
            >
              <Download className="w-4 h-4" /> Install App
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
